import { JourneyEvent } from "../api/analyticsAdmin";
import { parseEventProperties } from "./analyticsPlus";
import { EventCategory, CATEGORY_META } from "./analyticsPlusJourney";

export const HIDDEN_EVENTS = new Set<string>([
  "search_cleared",
  "business_modal_closed",
  "home_viewed",
]);

export const isHiddenEvent = (name: string): boolean =>
  name.startsWith("$") || HIDDEN_EVENTS.has(name);

export const prettify = (s: string): string => {
  const cleaned = s.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const CATEGORY_SLUG_LABELS: Record<string, string> = {
  hus_have: "Hus & have",
  mad_drikke: "Mad & drikke",
  oplevelser_underholdning: "Oplevelser & underholdning",
  via_link: "Via link",
  card_linking: "Card linking",
};

const slugLabel = (slug: string): string => CATEGORY_SLUG_LABELS[slug] ?? prettify(slug);

const accurateOffers = (n: number): string => `${n} accurate offer${n === 1 ? "" : "s"}`;

const asString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const buildBusinessNameMap = (events: JourneyEvent[]): Map<string, string> => {
  const map = new Map<string, string>();
  const addName = (id: unknown, name: unknown) => {
    if (typeof id === "string" && typeof name === "string") {
      const trimmedName = name.trim();
      if (id && trimmedName) map.set(id, trimmedName);
    }
  };
  events.forEach((event) => {
    const props = parseEventProperties(event.propertiesJson);
    addName(props["business_id"], props["business_name"]);
    ["accurate_top", "interested_top"].forEach((key) => {
      const arr = props[key];
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          if (item && typeof item === "object") {
            addName(
              (item as Record<string, unknown>)["id"],
              (item as Record<string, unknown>)["name"]
            );
          }
        });
      }
    });
  });
  return map;
};

export interface HumanizedEvent {
  text: string;
  category: EventCategory;
}

export const humanizeEvent = (
  event: JourneyEvent,
  businessNames: Map<string, string>
): HumanizedEvent => {
  const p = parseEventProperties(event.propertiesJson);

  const businessName = (): string => {
    const direct = asString(p["business_name"]);
    if (direct) return direct;
    const id = p["business_id"];
    if (typeof id === "string") {
      const resolved = businessNames.get(id);
      if (resolved) return resolved;
    }
    return "";
  };

  switch (event.event) {
    case "search_performed": {
      const query = asString(p["query"]);
      const total = typeof p["total_count"] === "number" ? (p["total_count"] as number) : null;
      const zero =
        p["total_zero_results"] === true || p["is_zero_result"] === true || total === 0;
      const accurate = typeof p["accurate_count"] === "number" ? (p["accurate_count"] as number) : 0;
      const interested = typeof p["interested_count"] === "number" ? (p["interested_count"] as number) : 0;
      if (!query) return { text: "Browsed offers", category: "search" };
      if (zero) return { text: `Searched "${query}" — no results`, category: "search" };
      if (interested > 0) {
        return {
          text: `Searched "${query}" — ${accurateOffers(accurate)}, ${interested} you might be interested in`,
          category: "search",
        };
      }
      return { text: `Searched "${query}" — ${accurateOffers(accurate)}`, category: "search" };
    }
    case "filter_changed": {
      const isOfferType = p["filter_type"] === "offer_type";
      if (p["action"] === "clear") {
        return {
          text: isOfferType ? "Cleared offer-type filter" : "Cleared category filter",
          category: "filter",
        };
      }
      const label = slugLabel(asString(p["filter_value"]));
      return {
        text: isOfferType ? `Selected offer type "${label}"` : `Selected category "${label}"`,
        category: "filter",
      };
    }
    case "business_opened": {
      const name = businessName();
      return { text: name ? `Opened business ${name}` : "Opened a business", category: "business" };
    }
    case "outbound_clicked": {
      const name = businessName();
      return {
        text: name ? `Clicked business ${name} external link` : "Clicked an external link",
        category: "outbound",
      };
    }
    case "typeahead_suggestion_clicked": {
      const suggestion = asString(p["suggestion_value"]);
      return {
        text: suggestion ? `Picked suggestion "${suggestion}"` : "Picked a suggestion",
        category: "search",
      };
    }
    case "app_opened":
      return { text: "Opened the app", category: "navigation" };
    case "onboarding_started":
      return { text: "Started onboarding", category: "auth" };
    case "onboarding_step_completed": {
      const step = asString(p["step_name"]);
      return {
        text: step ? `Completed onboarding step "${step}"` : "Completed an onboarding step",
        category: "auth",
      };
    }
    case "login_modal_opened":
      return { text: "Opened login", category: "auth" };
    case "login_started":
      return { text: "Started login", category: "auth" };
    case "become_customer_clicked":
      return { text: 'Clicked "Become customer"', category: "auth" };
    default:
      return { text: prettify(event.event), category: "other" };
  }
};

export interface HumanizedRow {
  key: string;
  text: string;
  category: EventCategory;
  color: string;
  firstTimestamp: string;
  count: number;
  events: JourneyEvent[];
}

export const buildHumanizedRows = (
  events: JourneyEvent[],
  businessNames: Map<string, string>
): HumanizedRow[] => {
  const rows: HumanizedRow[] = [];
  events.forEach((event) => {
    if (isHiddenEvent(event.event)) return;
    const { text, category } = humanizeEvent(event, businessNames);
    const last = rows[rows.length - 1];
    if (last && last.text === text) {
      last.count += 1;
      last.events.push(event);
    } else {
      rows.push({
        key: event.uuid,
        text,
        category,
        color: CATEGORY_META[category].color,
        firstTimestamp: event.timestamp,
        count: 1,
        events: [event],
      });
    }
  });
  return rows;
};
